package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxSnackbar
import fr.frux.core.designsystem.components.InterceptNavigationBack
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.maps.Location
import fr.frux.feature.treeadd.rememberContributionGuide
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.treeadd_error_create
import fr.frux.feature.treeadd.resources.treeadd_retry
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_sent
import fr.frux.shared.domain.model.Tree
import org.jetbrains.compose.resources.stringResource

private val SNACKBAR_ABOVE_FOOTER = 88.dp

/**
 * Hôte de l'assistant de création. À l'étape Placer il pilote la visée de la carte persistante
 * par [aim] (démarrée à l'entrée de l'étape, arrêtée à la sortie, y compris quand l'écran quitte
 * la composition). [locateButton] est fourni par composeApp, qui seul connaît le bouton de
 * localisation de la carte.
 */
@Composable
fun TreeWizardScreen(
    start: TreeWizardStart,
    aim: TreeWizardAim,
    locateButton: @Composable () -> Unit,
    onExit: () -> Unit,
    onTreeAdded: (Tree, Boolean, Boolean) -> Unit,
    onExistingTreeSelected: (Long) -> Unit,
    onSiteChosen: (Location) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: TreeWizardViewModel = rememberTreeWizardViewModel(start),
) {
    val state by viewModel.state.collectAsState()
    val currentAim by rememberUpdatedState(aim)
    // Callbacks relus à chaque événement : sur le web les libellés qu'ils capturent arrivent après
    // la première composition (#511).
    val currentOnExit by rememberUpdatedState(onExit)
    val currentOnTreeAdded by rememberUpdatedState(onTreeAdded)
    val currentOnExisting by rememberUpdatedState(onExistingTreeSelected)
    val currentOnSiteChosen by rememberUpdatedState(onSiteChosen)

    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                is TreeWizardEvent.Created -> {
                    currentOnTreeAdded(event.tree, event.photoUploadFailed, event.firstContribution)
                }

                is TreeWizardEvent.ExistingTreeSelected -> {
                    currentOnExisting(event.treeId)
                }

                is TreeWizardEvent.SiteChosen -> {
                    currentOnSiteChosen(event.position)
                }

                TreeWizardEvent.Exited -> {
                    currentOnExit()
                }
            }
        }
    }

    val onPlace = state.step == WizardStep.PLACE
    DisposableEffect(onPlace) {
        if (onPlace) currentAim.start(viewModel.rememberedAim ?: viewModel.state.value.position)
        onDispose {
            if (onPlace) {
                // Remontage seulement : à la sortie par « Suivant » l'étape a déjà changé et la
                // position figée fait foi.
                if (viewModel.state.value.step == WizardStep.PLACE) {
                    currentAim.currentTarget()?.let { viewModel.rememberedAim = it }
                }
                currentAim.stop()
            }
        }
    }

    // Étapes 2 et 3 : la position figée reste marquée sur la carte visible à côté (grand écran, #629).
    FrozenPositionEffect(aim, state)

    if (state.exitPrompt != null) {
        InterceptNavigationBack(viewModel::dismissExit)
    } else if (!onPlace) {
        InterceptNavigationBack(viewModel::goBack)
    } else if (!state.lostInputs().isEmpty) {
        // Retour 3 puis 2 puis 1 : les saisies survivent à l'étape Placer, un retour système ne doit
        // pas les perdre sans dialogue.
        InterceptNavigationBack(viewModel::requestExit)
    }

    rememberContributionGuide(state.introVisible, viewModel::acknowledgeIntro)

    val snackbarHostState = remember { SnackbarHostState() }
    WizardSnackbars(state, viewModel, snackbarHostState)

    Box(modifier = modifier.fillMaxSize()) {
        WizardStepContent(state, viewModel, aim, locateButton)
        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = SNACKBAR_ABOVE_FOOTER),
        ) { FruxSnackbar(it) }
    }

    state.exitPrompt?.let { lost ->
        WizardExitDialog(lost = lost, onQuit = viewModel::confirmExit, onContinue = viewModel::dismissExit)
    }
}

@Composable
private fun FrozenPositionEffect(
    aim: TreeWizardAim,
    state: TreeWizardState,
) {
    val currentAim by rememberUpdatedState(aim)
    val position = if (state.step == WizardStep.PLACE) null else state.position
    DisposableEffect(position) {
        currentAim.showFrozen(position)
        onDispose { currentAim.showFrozen(null) }
    }
}

@Composable
private fun WizardStepContent(
    state: TreeWizardState,
    viewModel: TreeWizardViewModel,
    aim: TreeWizardAim,
    locateButton: @Composable () -> Unit,
) {
    val expanded = LocalFruxLayoutMode.current == FruxLayoutMode.Expanded
    when (state.step) {
        WizardStep.PLACE -> {
            if (expanded) {
                PlaceStepExpanded(
                    candidate = aim.candidate,
                    onTree = { confirmAimedPosition(aim, viewModel) },
                    onSite = { chooseAimedSite(aim, viewModel) },
                    onCancel = viewModel::requestExit,
                    onOpenCandidate = viewModel::selectExistingTree,
                )
            } else {
                PlaceStepCompact(
                    candidate = aim.candidate,
                    locateButton = locateButton,
                    onTree = { confirmAimedPosition(aim, viewModel) },
                    onSite = { chooseAimedSite(aim, viewModel) },
                    onCancel = viewModel::requestExit,
                    onOpenCandidate = viewModel::selectExistingTree,
                )
            }
        }

        WizardStep.SPECIES -> {
            Column(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background).imePadding()) {
                WizardHeader(
                    step = WizardStep.SPECIES,
                    onBack = viewModel::goBack,
                    onCancel = viewModel::requestExit,
                )
                SpeciesStep(
                    state = state.species,
                    controller = viewModel.species,
                    onNext = viewModel::goToDetails,
                    onExistingTreeSelected = viewModel::selectExistingTree,
                    modifier = Modifier.weight(1f),
                )
            }
        }

        WizardStep.DETAILS -> {
            Column(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background).imePadding()) {
                WizardHeader(
                    step = WizardStep.DETAILS,
                    onBack = viewModel::goBack,
                    onCancel = viewModel::requestExit,
                )
                DetailsStep(
                    state = state,
                    controller = viewModel.details,
                    onSubmit = viewModel::submit,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

@Composable
private fun WizardSnackbars(
    state: TreeWizardState,
    viewModel: TreeWizardViewModel,
    snackbarHostState: SnackbarHostState,
) {
    // Échec de création : la même clé d'idempotence rejoue la création, sans doublon.
    SnackbarMessageEffect(
        trigger = state.submitFailed,
        message = if (state.submitFailed) stringResource(Res.string.treeadd_error_create) else null,
        snackbarHostState = snackbarHostState,
        actionLabel = stringResource(Res.string.treeadd_retry),
    ) { result ->
        viewModel.consumeSubmitError()
        if (result == SnackbarResult.ActionPerformed) viewModel.submit()
    }
    // One-shot de l'étape Espèce, comme l'échec ci-dessus : le texte attend son chargement (js).
    val suggestionSent = state.species.suggestion.sent
    SnackbarMessageEffect(
        trigger = suggestionSent,
        message = if (suggestionSent) stringResource(Res.string.treeadd_species_suggest_sent) else null,
        snackbarHostState = snackbarHostState,
    ) { viewModel.species.consumeSuggestionSent() }
}

private fun confirmAimedPosition(
    aim: TreeWizardAim,
    viewModel: TreeWizardViewModel,
) {
    aim.currentTarget()?.let(viewModel::confirmPosition)
}

private fun chooseAimedSite(
    aim: TreeWizardAim,
    viewModel: TreeWizardViewModel,
) {
    aim.currentTarget()?.let(viewModel::chooseSite)
}
